import { useState, useRef, useEffect } from "react";
import type { MouseEvent } from "react";
import {
  Box,
  Paper,
  Fab,
  IconButton,
  TextField,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  Badge,
} from "@mui/material";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import HistoryIcon from "@mui/icons-material/History";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import CloseFullscreenRoundedIcon from "@mui/icons-material/CloseFullscreenRounded";
import OpenInFullRoundedIcon from "@mui/icons-material/OpenInFullRounded";
import { useAppSelector } from "../../../store/types";
import { apiFetch } from "../../../store/services/api";
import {
  CHAT_MAX_CHATS,
  CHAT_MAX_MESSAGES,
  newChatId,
  loadChatHistory,
  saveActiveChat,
  relativeTime,
  chatPreview,
  type StoredChat as StoredChatBase,
} from "../../utils/chatHistory";

/**
 * Floating "Ask about this candidate" chat widget on the profile page.
 *
 * Backend reuses the Agent Workflow engine (the same persona/SOP/tools that
 * draft emails) for interactive Q&A grounded in the candidate being viewed.
 * Stateless backend: the widget owns the conversation and POSTs the full
 * message list each turn. The current profileId scopes every answer server-side.
 */
interface ChatMsg {
  role: "user" | "assistant";
  content: string;
}

// Chat history is localStorage-backed and scoped per candidate — see the
// shared store in ../../utils/chatHistory (5 chats/candidate, 30 msgs/chat,
// oldest evicted). MAX_MESSAGES is re-aliased for the in-thread send cap.
const MAX_MESSAGES = CHAT_MAX_MESSAGES;
const MAX_CHATS = CHAT_MAX_CHATS;
type StoredChat = StoredChatBase<ChatMsg>;
const chatStorageKey = (profileId: string) => `profileChat:v1:${profileId}`;

export default function ProfileChatWidget({ profileId }: { profileId: string }) {
  const environment = useAppSelector((s) => s.environment.current);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentChatId, setCurrentChatId] = useState<string>(() => newChatId());
  const [history, setHistory] = useState<StoredChat[]>([]);
  const [historyAnchor, setHistoryAnchor] = useState<null | HTMLElement>(null);
  // Minimized-launcher preference — shared with the global Talent AI widget via
  // one localStorage key (Faiz 2026-07-31: the labeled pill covered the
  // interview panel's action buttons). Minimized = small icon-only FAB, no
  // pulse animation, tooltip keeps it discoverable; expand it back from the
  // chat panel header.
  const [miniLauncher, setMiniLauncher] = useState<boolean>(() => {
    try { return localStorage.getItem("talent_ai_launcher_mini") === "1"; } catch { return false; }
  });
  const setMiniPersist = (v: boolean) => {
    setMiniLauncher(v);
    try { localStorage.setItem("talent_ai_launcher_mini", v ? "1" : "0"); } catch { /* ignore */ }
  };

  const listRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Backend is stateless — start a fresh chat when the recruiter switches
  // candidates, and load that candidate's saved history so it's ready behind
  // the History button.
  useEffect(() => {
    setMessages([]);
    setError(null);
    setCurrentChatId(newChatId());
    setHistory(loadChatHistory<ChatMsg>(chatStorageKey(profileId)));
  }, [profileId]);

  // Auto-persist the active conversation (capped: MAX_MESSAGES/chat,
  // MAX_CHATS/profile — oldest evicted). Empty chats are never stored.
  useEffect(() => {
    if (!profileId || messages.length === 0) return;
    setHistory(saveActiveChat<ChatMsg>(chatStorageKey(profileId), currentChatId, messages));
  }, [messages, profileId, currentChatId]);

  const startNewChat = () => {
    // The prior conversation is already auto-saved; just open a blank one.
    setMessages([]);
    setError(null);
    setCurrentChatId(newChatId());
    setHistory(loadChatHistory<ChatMsg>(chatStorageKey(profileId)));
    setHistoryAnchor(null);
    inputRef.current?.focus();
  };

  const openStoredChat = (chat: StoredChat) => {
    setMessages(chat.messages);
    setCurrentChatId(chat.id);
    setError(null);
    setHistoryAnchor(null);
  };

  const openHistoryMenu = (e: MouseEvent<HTMLElement>) => {
    setHistory(loadChatHistory<ChatMsg>(chatStorageKey(profileId)));
    setHistoryAnchor(e.currentTarget);
  };

  // Keep the newest message in view.
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, loading, open]);

  // Keep focus in the input. The field is disabled while a request is in
  // flight (which drops focus); refocus when the panel opens and once a send
  // completes so the recruiter can keep typing without clicking back in.
  useEffect(() => {
    if (open && !loading) inputRef.current?.focus();
  }, [open, loading]);

  const send = async () => {
    const q = input.trim();
    if (!q || loading || !profileId) return;
    // Cap the conversation at MAX_MESSAGES — this is both what we persist and
    // what we send to the backend, so a long thread drops its oldest turns.
    const next: ChatMsg[] = [...messages, { role: "user", content: q }].slice(-MAX_MESSAGES);
    setMessages(next);
    setInput("");
    setLoading(true);
    setError(null);
    try {
      const res = (await apiFetch("/api/agent-workflow/profile-chat", {
        environment,
        method: "POST",
        body: { profileId, messages: next },
      })) as { answer?: string; toolsUsed?: string[] };
      setMessages([...next, { role: "assistant", content: res?.answer || "(no answer returned)" }].slice(-MAX_MESSAGES));
    } catch (err: any) {
      // Leave the user's message in place so they can retry.
      setError(err?.message || "Failed to get an answer");
    } finally {
      setLoading(false);
      // Keep the cursor in the input so the recruiter can type the next
      // question immediately without clicking back in.
      inputRef.current?.focus();
    }
  };

  if (!profileId) return null;

  if (!open) {
    // Minimized: a small icon-only FAB — always visible, tooltip-labeled, one
    // click opens the chat. The tiny corner badge expands the labeled launcher
    // back directly from the icon (no need to open the chat first).
    if (miniLauncher) {
      return (
        <Box sx={{ position: "fixed", bottom: 56, right: 24, zIndex: 1200 }}>
          <Tooltip title="Ask about this candidate" placement="left" arrow>
            <Fab
              size="small"
              onClick={() => setOpen(true)}
              sx={{ bgcolor: "#2563EB", color: "#fff", "&:hover": { bgcolor: "#1D4ED8" } }}
            >
              <SmartToyOutlinedIcon fontSize="small" />
            </Fab>
          </Tooltip>
          <Tooltip title="Show the labeled launcher" arrow>
            <IconButton
              size="small"
              aria-label="Expand assistant launcher"
              onClick={(e) => { e.stopPropagation(); setMiniPersist(false); }}
              sx={{
                position: "absolute", top: -9, left: -9, width: 20, height: 20, p: 0,
                bgcolor: "#fff", border: "1px solid rgba(0,0,0,0.14)", boxShadow: 1,
                "&:hover": { bgcolor: "#EFF6FF" },
              }}
            >
              <OpenInFullRoundedIcon sx={{ fontSize: 11, color: "#1D4ED8" }} />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }
    return (
      <Box
        onClick={() => setOpen(true)}
        sx={{ position: "fixed", bottom: 56, right: 24, zIndex: 1200, display: "flex", alignItems: "center", gap: 1, cursor: "pointer" }}
      >
        {/* Attention pill — gently bobs to invite a click. Hidden on tiny screens. */}
        <Paper
          elevation={3}
          sx={{
            px: 1.5,
            py: 0.75,
            borderRadius: 5,
            bgcolor: "#fff",
            border: "1px solid rgba(37,99,235,0.25)",
            display: { xs: "none", sm: "flex" },
            alignItems: "center",
            gap: 0.75,
            whiteSpace: "nowrap",
            animation: "pcwBob 2.4s ease-in-out infinite",
            "@keyframes pcwBob": {
              "0%, 100%": { transform: "translateY(0)" },
              "50%": { transform: "translateY(-3px)" },
            },
          }}
        >
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#1D4ED8" }}>
            💬 Ask about this candidate
          </Typography>
          <Tooltip title="Minimize — keeps the round button" arrow>
            <IconButton
              size="small"
              aria-label="Minimize assistant launcher"
              onClick={(e) => { e.stopPropagation(); setMiniPersist(true); }}
              sx={{ p: 0.25, color: "#93A6C8", "&:hover": { color: "#1D4ED8", bgcolor: "rgba(37,99,235,0.08)" } }}
            >
              <CloseFullscreenRoundedIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Paper>
        <Tooltip title="Ask about this candidate" placement="left" arrow>
          <Fab
            sx={{
              bgcolor: "#2563EB",
              color: "#fff",
              "&:hover": { bgcolor: "#1D4ED8" },
              animation: "pcwPulse 2.4s ease-in-out infinite",
              "@keyframes pcwPulse": {
                "0%": { boxShadow: "0 0 0 0 rgba(37,99,235,0.45)" },
                "70%": { boxShadow: "0 0 0 14px rgba(37,99,235,0)" },
                "100%": { boxShadow: "0 0 0 0 rgba(37,99,235,0)" },
              },
            }}
          >
            <SmartToyOutlinedIcon />
          </Fab>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Paper
      elevation={8}
      sx={{
        position: "fixed",
        bottom: 56,
        right: 24,
        zIndex: 1200,
        width: 380,
        maxWidth: "calc(100vw - 32px)",
        height: 520,
        maxHeight: "calc(100vh - 96px)",
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1.25, bgcolor: "#2563EB", color: "#fff" }}>
        <SmartToyOutlinedIcon sx={{ fontSize: 20 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.2 }}>Ask about this candidate</Typography>
          <Typography sx={{ fontSize: 11, opacity: 0.85 }}>AI answers grounded in this profile's data</Typography>
        </Box>
        <Tooltip title={miniLauncher ? "Show the labeled launcher again" : "Minimize the launcher to a small button"} arrow>
          <IconButton size="small" onClick={() => setMiniPersist(!miniLauncher)} sx={{ color: "#fff" }} aria-label="Toggle launcher size">
            {miniLauncher ? <OpenInFullRoundedIcon fontSize="small" /> : <CloseFullscreenRoundedIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title="New chat" arrow>
          <IconButton size="small" onClick={startNewChat} sx={{ color: "#fff" }} aria-label="New chat">
            <AddCommentOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Chat history" arrow>
          <IconButton size="small" onClick={openHistoryMenu} sx={{ color: "#fff" }} aria-label="Chat history">
            <Badge
              badgeContent={history.length}
              color="error"
              sx={{ "& .MuiBadge-badge": { fontSize: 9, height: 15, minWidth: 15 } }}
            >
              <HistoryIcon fontSize="small" />
            </Badge>
          </IconButton>
        </Tooltip>
        <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: "#fff" }} aria-label="Close chat">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Messages */}
      <Box
        ref={listRef}
        sx={{ flex: 1, overflowY: "auto", p: 1.5, bgcolor: "#F9FAFB", display: "flex", flexDirection: "column", gap: 1 }}
      >
        {messages.length === 0 && !loading && (
          <Box sx={{ m: "auto", textAlign: "center", color: "text.secondary", px: 2 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>Ask anything about this candidate</Typography>
            <Typography sx={{ fontSize: 12 }}>
              e.g. "What stage are they in?", "Last interview rating?", "Who is their account manager?"
            </Typography>
          </Box>
        )}
        {messages.map((m, i) => (
          <Box
            key={i}
            sx={{
              alignSelf: m.role === "user" ? "flex-end" : "flex-start",
              maxWidth: "85%",
              bgcolor: m.role === "user" ? "#2563EB" : "#FFFFFF",
              color: m.role === "user" ? "#fff" : "text.primary",
              border: m.role === "user" ? "none" : "1px solid rgba(17,24,39,0.08)",
              borderRadius: 2,
              px: 1.25,
              py: 0.75,
            }}
          >
            <Typography sx={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{m.content}</Typography>
          </Box>
        ))}
        {loading && (
          <Box sx={{ alignSelf: "flex-start", display: "flex", alignItems: "center", gap: 1, color: "text.secondary", px: 1 }}>
            <CircularProgress size={14} />
            <Typography sx={{ fontSize: 12 }}>Thinking…</Typography>
          </Box>
        )}
      </Box>

      {/* Input */}
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.5, p: 1, borderTop: "1px solid rgba(17,24,39,0.08)", bgcolor: "#fff" }}>
        <TextField
          inputRef={inputRef}
          autoFocus
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder="Ask about this candidate…"
          size="small"
          fullWidth
          multiline
          maxRows={4}
          sx={{ "& .MuiInputBase-input": { fontSize: 13 } }}
        />
        <IconButton color="primary" onClick={send} disabled={loading || !input.trim()} sx={{ color: "#2563EB" }} aria-label="Send">
          <SendIcon fontSize="small" />
        </IconButton>
      </Box>

      <Snackbar
        open={!!error}
        autoHideDuration={5000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setError(null)} sx={{ fontSize: 12 }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Chat history — up to MAX_CHATS previous conversations for this
          candidate. Click one to reopen it; "New chat" starts a fresh one. */}
      <Menu
        anchorEl={historyAnchor}
        open={Boolean(historyAnchor)}
        onClose={() => setHistoryAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: 300, maxWidth: "90vw" } } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary" }}>
            Recent chats ({history.length}/{MAX_CHATS})
          </Typography>
        </Box>
        <Divider />
        {history.length === 0 && (
          <MenuItem disabled sx={{ fontSize: 13 }}>No previous chats</MenuItem>
        )}
        {history.map((chat) => (
          <MenuItem
            key={chat.id}
            selected={chat.id === currentChatId}
            onClick={() => openStoredChat(chat)}
            sx={{ display: "block", py: 1, whiteSpace: "normal" }}
          >
            <Typography
              sx={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
              {chatPreview(chat)}
            </Typography>
            <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
              {relativeTime(chat.updatedAt)} · {chat.messages.length} msg{chat.messages.length === 1 ? "" : "s"}
              {chat.id === currentChatId ? " · current" : ""}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </Paper>
  );
}
